/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ------------------------------------------------------------------------------------------------------------
 * Generic reveal on scroll handler
 * ------------------------------------------------------------------------------------------------------------
 */
(function () {
  const initRevealOnScroll = () => {
    const revealOnScrollElems = document.querySelectorAll(
      '[data-reveal-on-scroll]:not([data-reveal-on-scroll=""]'
    );

    const REVEAL_CLASS = "reveal-fade-up";
    let counter = revealOnScrollElems.length;
    const callback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const revealClass =
            entry.target.dataset.revealOnScroll || REVEAL_CLASS;
          if (!entry.target.classList.contains(revealClass)) {
            entry.target.classList.add(revealClass);
            observer.unobserve(entry.target);
            counter--;

            if (!counter) {
              observer.disconnect();
            }
          }
        }
      });
    };
    const revealObserver = new IntersectionObserver(callback);

    revealOnScrollElems.forEach((elem) => {
      revealObserver.observe(elem);
    });

    return revealObserver;
  };
  const disconnectObserver = (observer) => {
    if (observer && observer.disconnect) {
      observer.disconnect();
    }
  };
  const onShopifySectionEvent = (observer) => {
    disconnectObserver(observer);
    mainObserver = initRevealOnScroll();
  };
  let mainObserver = {};

  document.addEventListener("DOMContentLoaded", () => {
    mainObserver = initRevealOnScroll();
  });
  document.addEventListener("shopify:section:unload", () => {
    onShopifySectionEvent(mainObserver);
  });
  document.addEventListener("shopify:section:load", () => {
    onShopifySectionEvent(mainObserver);
  });
})();
/* ------------------------------------------------------------------------------------------------------------
 * Klaviyo Added to Cart Tracking
 * ------------------------------------------------------------------------------------------------------------
 */
(function () {
  document.addEventListener("variant:added", function (event) {
    const detail = event && event.detail ? event.detail : {};
    const src = detail.src;
    const cart = detail.cart;
    const cartVariant = detail.variant;
    let cartData = {};

    // Klaviyo created variables
    let addedItem = window.item;
    let _learnq = window._learnq || [];

    // Update Klaviyo created item price. Klaviyo injected snippet uses product
    // price which may not be the actual price of the added varaint.
    if (addedItem && cartVariant) {
      addedItem.Price = window.RS.helpers.formatMoney(cartVariant.price);
    }

    // If not on the pdp or is the main product form on the pdp and the Klaviyo
    // item object does not exist, mimic Klaviyo item object as best as possible.
    // Some properties are not available from JS Shopify object.
    if (src !== "pdp" || (src === "pdp" && !addedItem)) {
      if (cartVariant) {
        addedItem = {
          Brand: cartVariant.vendor,
          ImageURL: cartVariant.image,
          Name: cartVariant.product_title,
          Price: window.RS.helpers.formatMoney(cartVariant.price),
          ProductID: cartVariant.product_id,
          URL: `${window.themeVariables.routes.secureShopUrl}${cartVariant.url}`,
        };
      }
    }

    if (addedItem) {
      addedItem.src = src;
    }

    if (cart) {
      // Format from Klaviyo docs:
      // https://help.klaviyo.com/hc/en-us/articles/115001396711-Guide-to-Creating-an-Added-to-Cart-Event-for-Shopify
      cartData = {
        total_price: cart.total_price / 100,
        $value: cart.total_price / 100,
        total_discount: cart.total_discount,
        original_total_price: cart.original_total_price / 100,
        items: cart.items,
      };
    }

    _learnq.push([
      "track",
      "Added to Cart",
      {
        ...(addedItem || {}),
        ...cartData,
      },
    ]);
  });
})();

/* ------------------------------------------------------------------------------------------------------------
 * PDP Breadcrumb
 * ------------------------------------------------------------------------------------------------------------
 */
window.RS = window.RS || {};
RS.localStorage = (function () {
  const BREADCRUMB = "breadcrumb";
  const ANCHOR_EL = `#${BREADCRUMB} .breadcrumb__item.placeholder`;
  const HIDDEN = "hidden";
  const PREFIX = "products";
  const CEIL = 2;
  let localArray = [];
  let HOME;

  const renderAnchor = function ({ title, url }) {
    return `<a href="${url}">${title}</a>`;
  };

  const hide = function (el, bool) {
    hidden = bool;
    el.ariaHidden = hidden.toString();
    if (bool) {
      el.classList.add(HIDDEN);
    } else {
      el.classList.remove(HIDDEN);
    }
  };

  const render = function () {
    const el = document.querySelector(ANCHOR_EL);
    const breadcrumb = localStorage.getItem(BREADCRUMB);
    const localArray = JSON.parse(breadcrumb);

    if (!el) return;
    if (localArray?.length === CEIL) {
      const { title, url } = localArray?.[0];
      if (title !== document.title && !url.includes(PREFIX) && url !== HOME) {
        hide(el, false);
        el.innerHTML = renderAnchor({
          title,
          url,
        });
      } else {
        hide(el, true);
        el.innerHTML = "";
      }
    } else {
      hide(el, true);
    }
  };
  const initialize = function () {
    HOME = `${window.location.origin}/`;
    const title = document.title.split("|")[0];
    const local = {
      title,
      url: window.location.href,
    };

    const breadcrumb = localStorage.getItem(BREADCRUMB);
    let array = JSON.parse(breadcrumb);
    if (!breadcrumb || array.length === 0) {
      localArray.push(local);
    } else {
      if (array?.length > 0 && array?.length < CEIL) {
        array.push(local);
        localArray = [...array];
      } else if (array?.length >= CEIL) {
        if (array?.[CEIL - 1].title !== local.title) {
          localArray = [array?.[1], local];
        } else {
          localArray = [...array];
        }
      }
    }
    localStorage.setItem(BREADCRUMB, JSON.stringify(localArray));
    render();
  };

  return {
    initialize,
  };
})();
document.addEventListener("DOMContentLoaded", function () {
  RS.localStorage.initialize();
});

/* ------------------------------------------------------------------------------------------------------------
 * Scroll to anchor on page load
 * ------------------------------------------------------------------------------------------------------------
 */
(() => {
  document.addEventListener("DOMContentLoaded", () => {
    const hash = window.location.hash;

    // Check if the hash exists and corresponds to an element with that ID
    const targetElement = hash ? document.querySelector(hash) : null;

    if (targetElement) {
      // Calculate the offset to account for a fixed header or other spacing
      const offset = 100;

      // Calculate the target position
      const targetPosition =
        targetElement.getBoundingClientRect().top + window.scrollY - offset;

      // Smooth scroll to the target position
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  });
})();

/* ------------------------------------------------------------------------------------------------------------
 * Canada announcement bar (announcement-bar-canada.liquid)
 * ------------------------------------------------------------------------------------------------------------
 */
(() => {
  const SECTION_CLASS = "shopify-section--announcement-bar-canada";
  const SEL = {
    section: `.${SECTION_CLASS}`,
    inner: "section[data-announcement-bar-canada]",
    bar: "announcement-bar",
    item: "announcement-bar-item",
    nav: '[data-action="prev"], [data-action="next"]',
  };

  function storefrontRoot() {
    const r = window.themeVariables?.routes?.rootUrl || window.Shopify?.routes?.root;
    if (r == null || r === "") return null;
    const s = String(r);
    return s.endsWith("/") ? s : `${s}/`;
  }

  function geoSuggestionsPath(root) {
    const u = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);
    u.searchParams.set("country[enabled]", "true");
    return `${u.pathname}${u.search}`;
  }

  function countryHandleFromSuggestions(data) {
    const h =
      data?.detected_values?.country?.handle ??
      data?.suggestions?.[0]?.parts?.country?.handle;
    return h ? String(h).toUpperCase() : null;
  }

  function setAnnouncementBarHeightVar(sectionEl) {
    document.documentElement.style.setProperty(
      "--announcement-bar-height",
      sectionEl ? `${sectionEl.clientHeight}px` : "0px"
    );
  }

  function lockNode(node) {
    node.setAttribute("hidden", "");
    node.style.setProperty("display", "none", "important");
  }

  function unlockNode(node) {
    node.removeAttribute("hidden");
    node.style.removeProperty("display");
  }

  function syncBarControls(bar, itemCount) {
    if (!bar) return;
    const nav = bar.querySelectorAll(SEL.nav);
    if (itemCount <= 1) {
      bar._pausePlayer?.();
      bar.removeAttribute("auto-play");
      bar.classList.remove("announcement-bar--multiple");
      nav.forEach((btn) => btn.setAttribute("hidden", ""));
    } else {
      bar.classList.add("announcement-bar--multiple");
      nav.forEach((btn) => btn.removeAttribute("hidden"));
    }
  }

  function hideCanadaSection(sectionEl) {
    const bar = sectionEl.querySelector(SEL.bar);
    const inner = sectionEl.querySelector(SEL.inner);
    bar?._pausePlayer?.();
    if (inner) lockNode(inner);
    lockNode(sectionEl);
    setAnnouncementBarHeightVar(null);
  }

  function showCanadaSectionIfHasItems(sectionEl) {
    const inner = sectionEl.querySelector(SEL.inner);
    const bar = sectionEl.querySelector(SEL.bar);
    if (!inner || !bar) return;
    unlockNode(sectionEl);
    const items = bar.querySelectorAll(SEL.item);
    if (items.length === 0) {
      lockNode(inner);
      setAnnouncementBarHeightVar(null);
      return;
    }
    unlockNode(inner);
    syncBarControls(bar, items.length);
    setAnnouncementBarHeightVar(sectionEl);
  }

  function initCanadaAnnouncementSection(sectionEl) {
    if (!sectionEl.querySelector(SEL.inner)) return;

    const root = storefrontRoot();
    if (!root) {
      showCanadaSectionIfHasItems(sectionEl);
      return;
    }

    fetch(geoSuggestionsPath(root), { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        const show =
          window.Shopify?.designMode || countryHandleFromSuggestions(data) === "CA";
        (show ? showCanadaSectionIfHasItems : hideCanadaSection)(sectionEl);
      })
      .catch(() => showCanadaSectionIfHasItems(sectionEl));
  }

  function initAllCanadaAnnouncementBars() {
    document.querySelectorAll(SEL.section).forEach(initCanadaAnnouncementSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllCanadaAnnouncementBars);
  } else {
    initAllCanadaAnnouncementBars();
  }
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.classList?.contains(SECTION_CLASS)) {
      initCanadaAnnouncementSection(event.target);
    }
  });
})();

/* ------------------------------------------------------------------------------------------------------------
Open Cart When Redirected from /cart
* ------------------------------------------------------------------------------------------------------------*/
(() => {
  const getSearchParam = (param) => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(param);
  };

  const clearSearchParam = (param) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(param);
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${searchParams}`
    );
  };

  if (getSearchParam("openCart") === "true") {
    document.addEventListener("rebuy:smartcart.ready", (event) => {
      Rebuy.SmartCart.show();
      clearSearchParam("openCart");
    });
  }
})();

/* ------------------------------------------------------------------------------------------------------------
ADA compliance enhancements
* ------------------------------------------------------------------------------------------------------------*/
(() => {
  // Set aria-label on the swym wishlist anchor
  function onSwymLoadCallback(swat) {
    document
      .getElementById("swym-anchor")
      .setAttribute("aria-label", "Go to wishlist");
  }

  if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
  }
  window.SwymCallbacks.push(onSwymLoadCallback);
})();


(() => {
  document.addEventListener("DOMContentLoaded", () => {
    const components = document.querySelectorAll("shopify-payment-terms");
    
    if (components.length > 0) {
      components.forEach((component) => {
        if (component && component.shadowRoot) {
          const style = document.createElement("style");
          style.textContent = `
            #shopify-installments-content, #shopify-installments-cta {
              font-family: var(--text-font-family);
              font-size: 15px;
              color: rgb(var(--text-color));
            }
            #shopify-installments-content .font-bold {
              font-weight: 500;
            }
          `;
          component.shadowRoot.prepend(style);
        }
      });
    }
  });
})();

/* ------------------------------------------------------------------------------------------------------------
 * ADA: Mega Menu Accessibility
 * ------------------------------------------------------------------------------------------------------------*/
(() => {
  document.addEventListener("DOMContentLoaded", () => {
    // Find all menu items with mega menus (more reliable than text matching)
    const megaMenuItems = document.querySelectorAll(
      ".header__linklist-item.has-dropdown"
    );

    megaMenuItems.forEach((menuItem) => {
      const button = menuItem.querySelector("[aria-controls]");
      if (!button) return;

      // Check if this menu item has a mega menu (not just regular dropdown)
      const dropdown = document.getElementById(
        button.getAttribute("aria-controls")
      );
      if (!dropdown || !dropdown.classList.contains("mega-menu")) return;

      // When focused, set to expanded
      button.addEventListener("focus", () => {
        button.setAttribute("aria-expanded", "true");
      });

      // Handle focus within the entire menu item (button + dropdown)
      menuItem.addEventListener("focusin", () => {
        button.setAttribute("aria-expanded", "true");
      });

      // Only close when focus leaves the entire menu item
      menuItem.addEventListener("focusout", (event) => {
        // Check if focus is moving to an element outside this menu item
        if (!menuItem.contains(event.relatedTarget)) {
        button.setAttribute("aria-expanded", "false");
        }
      });

      // Handle keyboard - Space toggles menu, Enter navigates
      button.addEventListener("keydown", (event) => {
        if (event.key === " ") {
          // Space toggles the menu
          event.preventDefault();
          const isExpanded = button.getAttribute("aria-expanded") === "true";
          button.setAttribute("aria-expanded", (!isExpanded).toString());
        }
        // Enter key will navigate normally (no preventDefault)
      });
    });

    // Global Escape key handler to close any open mega menu
    // Handles both keyboard navigation within menu and hover-opened menus
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        const openMegaMenus = document.querySelectorAll(
          '.header__linklist-item.has-dropdown [aria-controls][aria-expanded="true"]'
        );
        
        openMegaMenus.forEach((button) => {
          const dropdown = document.getElementById(
            button.getAttribute("aria-controls")
          );
          // Only close if it's a mega menu (not regular dropdown)
          if (dropdown && dropdown.classList.contains("mega-menu")) {
            // Simply close the menu
            button.setAttribute("aria-expanded", "false");
            dropdown.setAttribute("hidden", "");
            
            // If the event target is within this dropdown, return focus to the button
            if (dropdown.contains(event.target)) {
              button.focus();
            }
          }
        });
      }
    });
  });
})();